import { getLayoutByIdApi, WorkflowInstanceTaskType } from '@app-store'
import { Accordion, CustomButton, MODE_CONSTANTS } from '@ui-kit'
import React, { useEffect, useRef, useState } from 'react'
import { FieldValues } from 'react-hook-form'
import PAPCaptureInsuranceTask from './PAPCaptureInsuranceTask'
import FormRenderer from '../FormRenderer'
import { EntityLayout, LayoutData } from '../../types/layout.types'
import { noop } from 'lodash'
import {
  getEntityData,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import PAPReviewRequestCustom from './PAPReviewRequestCustom'
import BVCases from './BVCases'
import PatientInsuranceInfo from './PatientInsuranceInfo'
import { NAMESPACES, WORKFLOW_STATUS } from '../../utils/constants'
import {
  PAP_APPEAL_TASK_STRINGS,
  PAP_CONSTANTS,
} from '../../utils/pap.constants'
import { RootState, useAppSelector } from '../../utils/agent.store'
import { permissionObject } from '../../utils/helperFunctions'

const PAP_APPEAL_CONTAINER_LIST: FieldValues[] = [
  {
    key: PAP_CONSTANTS.REVIEW_REQUEST,
  },
  {
    key: PAP_CONSTANTS.DISCOVERY_DATA,
  },
  {
    key: PAP_CONSTANTS.BV_CASES,
  },
  {
    key: PAP_CONSTANTS.INSURANCE_INFO,
  },
  {
    key: PAP_CONSTANTS.E_VERIFICATION,
  },
  {
    key: PAP_CONSTANTS.CASE_PAP_DETAILS,
  },
  {
    key: PAP_CONSTANTS.LIS_INFO,
  },
]

const PAPAppealReviewTask: React.FC<{
  recordId: string
  task: WorkflowInstanceTaskType
}> = ({ recordId, task }) => {
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const [isViewMode, setIsViewMode] = useState<boolean>(false)
  const [lisLayout, setLisLayout] = useState<EntityLayout>()
  const [insuranceData, setInsuranceData] = useState<FieldValues>()
  const [papDetailsId, setPapDetailsId] = useState<number>()
  const [casePapDetailsLayout, setCasePapDetailsLayout] =
    useState<EntityLayout>()
  const [casePapDetailsData, setCasePapDetailsData] = useState<FieldValues>()
  const [eVerificationLayout, setEVerificationLayout] = useState<EntityLayout>()
  const [eVerificationData, setEVerificationData] = useState<FieldValues>()
  const customFormRef = useRef<{ submitForm: () => void }>(null)

  const fetchPAPDetailsId = async () => {
    try {
      const res = await getEntityDataByIdEmbedded(
        task?.identifier?.layout_info?.case?.entity_name ?? '',
        Number(task?.identifier?.layout_info?.case?.entity_id),
        ['case_pap_details'],
        recordId
      )
      if (res?.data) {
        setPapDetailsId(res?.data?.case_pap_details?.id)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchInsuranceLayout = async () => {
    try {
      const res = await getLayoutByIdApi({
        entityId: task?.identifier?.layout_info?.case?.entity_id,
        layoutId: task?.identifier?.layout_info?.layout_id,
      })
      if (res?.data) {
        setLisLayout(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchInsuranceData = async () => {
    try {
      const res = await getEntityData(
        task?.identifier?.layout_info?.case?.entity_name ?? '',
        Number(task?.identifier?.layout_info?.case?.entity_id),
        Number(task?.identifier?.layout_info?.layout_id),
        recordId
      )
      if (res?.data) {
        setInsuranceData(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }
  const fetchCasePapDetailsLayout = async () => {
    try {
      const res = await getLayoutByIdApi({
        entityId: task?.identifier?.layout_info?.casePapDetails?.entity_id,
        layoutId: task?.identifier?.layout_info?.casePapDetails?.layout_id,
      })
      if (res?.data) {
        setCasePapDetailsLayout(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }
  const fetchCasePapDetailsData = async () => {
    try {
      const res = await getEntityData(
        task?.identifier?.layout_info?.casePapDetails?.entity_name ?? '',
        Number(task?.identifier?.layout_info?.casePapDetails?.entity_id),
        Number(task?.identifier?.layout_info?.casePapDetails?.layout_id),
        Number(papDetailsId)
      )
      if (res?.data) {
        setCasePapDetailsData(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchEVerificationLayout = async () => {
    try {
      const res = await getLayoutByIdApi({
        entityId: task?.identifier?.layout_info?.eVerification?.entity_id,
        layoutId: task?.identifier?.layout_info?.eVerification?.layout_id,
      })
      if (res?.data) {
        setEVerificationLayout(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchEVerificationData = async () => {
    try {
      const res = await getEntityData(
        task?.identifier?.layout_info?.case?.entity_name ?? '',
        Number(task?.identifier?.layout_info?.case?.entity_id),
        Number(task?.identifier?.layout_info?.layout_id),
        recordId
      )
      if (res?.data) {
        setEVerificationData(res.data)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const hasEditPermission = (task: WorkflowInstanceTaskType) => {
    const hasServicePermission = permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      task,
      userId ?? ''
    )
    const hasIdentifierKey = task?.identifier?.task_permission
    if (hasIdentifierKey) {
      const hasTaskEditPermission =
        programPermissions[
          caseData?.programs?.id ?? workflowDefinition?.program_id ?? ''
        ]?.[hasIdentifierKey]?.Manage === true
      return hasTaskEditPermission
    } else {
      return hasServicePermission
    }
  }

  useEffect(() => {
    fetchPAPDetailsId()
    fetchInsuranceLayout()
    fetchInsuranceData()
    fetchCasePapDetailsLayout()
    fetchCasePapDetailsData()
    fetchEVerificationLayout()
    fetchEVerificationData()
  }, [task])

  useEffect(() => {
    if (papDetailsId) {
      fetchCasePapDetailsLayout()
      fetchCasePapDetailsData()
    }
  }, [papDetailsId])

  return (
    <div className="p-3 border border-tertiary-200 rounded-md">
      {PAP_APPEAL_CONTAINER_LIST.map((container) => {
        switch (container.key) {
          case PAP_CONSTANTS.REVIEW_REQUEST:
            return (
              <div>
                <div className="flex justify-end pb-3">
                  {hasEditPermission(task) && (
                    <CustomButton
                      onClick={
                        isViewMode
                          ? () => {
                              setIsViewMode(false)
                            }
                          : customFormRef?.current?.submitForm
                      }
                      disabled={task?.state !== WORKFLOW_STATUS.OPEN}
                      isFilled
                    >
                      {isViewMode ? NAMESPACES.EDIT : NAMESPACES.UPDATE_BUTTON}
                    </CustomButton>
                  )}
                </div>
                <Accordion
                  heading={
                    <div className="font-primary">
                      {PAP_APPEAL_TASK_STRINGS.REVIEW_APPEAL_REQUEST}{' '}
                    </div>
                  }
                >
                  <PAPReviewRequestCustom
                    ref={customFormRef}
                    papDetailsId={Number(papDetailsId)}
                    task={task}
                    recordId={recordId}
                    isViewMode={isViewMode}
                    setIsViewMode={setIsViewMode}
                  />
                </Accordion>
              </div>
            )
          case PAP_CONSTANTS.DISCOVERY_DATA:
            return (
              <Accordion
                heading={
                  <div className="font-primary">
                    {PAP_APPEAL_TASK_STRINGS.DISCOVERY_DATA}
                  </div>
                }
                alwaysOpen
              >
                <div className="px-3 pb-3">
                  <PAPCaptureInsuranceTask
                    recordId={recordId}
                    task={task}
                    isAppealTask={true}
                  />
                </div>
              </Accordion>
            )
          case PAP_CONSTANTS.LIS_INFO:
            return (
              <FormRenderer
                heading={null}
                layout={lisLayout?.layout_data as LayoutData}
                mode={MODE_CONSTANTS?.VIEW}
                data={insuranceData}
                handleFormSubmit={noop}
                fullLayout={lisLayout as EntityLayout}
              />
            )
          case PAP_CONSTANTS.BV_CASES:
            return <BVCases task={task} />
          case PAP_CONSTANTS.INSURANCE_INFO:
            return <PatientInsuranceInfo task={task} recordId={recordId} />
          case PAP_CONSTANTS.CASE_PAP_DETAILS:
            return (
              <FormRenderer
                heading={null}
                layout={casePapDetailsLayout?.layout_data as LayoutData}
                mode={MODE_CONSTANTS?.VIEW}
                data={casePapDetailsData}
                handleFormSubmit={noop}
                fullLayout={casePapDetailsLayout as EntityLayout}
              />
            )
          case PAP_CONSTANTS.E_VERIFICATION:
            return (
              <FormRenderer
                heading={null}
                layout={eVerificationLayout?.layout_data as LayoutData}
                mode={MODE_CONSTANTS?.VIEW}
                data={eVerificationData}
                handleFormSubmit={noop}
                fullLayout={eVerificationLayout as EntityLayout}
              />
            )
          default:
            return null
        }
      })}
    </div>
  )
}

export default PAPAppealReviewTask
